import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppLayout } from './layout';
import { ApiHttpError, internalFetch } from './lib/api';
import { supabase } from './lib/supabase';
import { AlertsPage } from './pages/Alerts';
import { BillingPage } from './pages/Billing';
import { CatalogPage } from './pages/Catalog';
import { CompaniesPage } from './pages/Companies';
import { CompanyDetailPage } from './pages/CompanyDetail';
import { DealDetailPage } from './pages/DealDetail';
import { PipelinePage } from './pages/Pipeline';
import { TodoPage } from './pages/Todo';
import { ClientDetail } from './pages/ClientDetail';
import { ClientsPage } from './pages/Clients';
import { ForbiddenPage } from './pages/Forbidden';
import { LoginPage } from './pages/Login';
import { OverviewPage } from './pages/Overview';
import { RequestDetail } from './pages/RequestDetail';
import { RequestsPage } from './pages/Requests';
import { SettingsPage } from './pages/Settings';
import { UseCasesPage } from './pages/UseCases';
import { WebhooksPage } from './pages/Webhooks';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/403',
  component: ForbiddenPage,
});

/** Pathless layout route guarding operator access (PRD 4.1). */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({ to: '/login' });
    }
    try {
      await internalFetch('/internal/me');
    } catch (error) {
      if (error instanceof ApiHttpError && error.status === 403) {
        throw redirect({ to: '/403' });
      }
      throw error;
    }
  },
  component: AppLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: OverviewPage,
});
const clientsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients',
  component: ClientsPage,
});
const clientDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients/$clientId',
  component: function ClientDetailRoute() {
    const { clientId } = clientDetailRoute.useParams();
    return <ClientDetail clientId={clientId} />;
  },
});
const requestsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/requests',
  component: RequestsPage,
});
const requestDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/requests/$requestId',
  component: function RequestDetailRoute() {
    const { requestId } = requestDetailRoute.useParams();
    return <RequestDetail requestId={requestId} />;
  },
});
const pipelineRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pipeline',
  component: PipelinePage,
});
const companiesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/companies',
  component: CompaniesPage,
});
const companyDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/companies/$companyId',
  component: function CompanyDetailRoute() {
    const { companyId } = companyDetailRoute.useParams();
    return <CompanyDetailPage key={companyId} companyId={companyId} />;
  },
});
const dealDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/deals/$dealId',
  component: function DealDetailRoute() {
    const { dealId } = dealDetailRoute.useParams();
    return <DealDetailPage key={dealId} dealId={dealId} />;
  },
});
const todoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/todo',
  component: TodoPage,
});
const catalogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/catalog',
  component: CatalogPage,
});
const useCasesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/use-cases',
  component: UseCasesPage,
});
const billingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/billing',
  component: BillingPage,
});
const webhooksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/webhooks',
  component: WebhooksPage,
});
const alertsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/alerts',
  component: AlertsPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forbiddenRoute,
  protectedRoute.addChildren([
    overviewRoute,
    clientsRoute,
    clientDetailRoute,
    requestsRoute,
    requestDetailRoute,
    pipelineRoute,
    companiesRoute,
    companyDetailRoute,
    dealDetailRoute,
    todoRoute,
    catalogRoute,
    useCasesRoute,
    billingRoute,
    webhooksRoute,
    alertsRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
